import React from "react";
import moment from "moment";

function pad(number) {
  if (number < 10) {
    return "0" + number;
  }
  return number;
}

const toBackendDateString = (date) =>
  date.getUTCFullYear()
  + "-"
  + pad(date.getUTCMonth() + 1)
  + "-"
  + pad(date.getUTCDate())
  + ":"
  + pad(date.getUTCHours())
  + ":"
  + pad(date.getUTCMinutes())
  + ":"
  + pad(date.getUTCSeconds());

const toDate = (value, fallback) => {
  if (value instanceof Date) {
    return value;
  }
  if (Number.isFinite(value)) {
    const date = new Date();
    date.setTime(value);
    return date;
  }
  return fallback;
};

const toISOStringWithoutMillis = (date) =>
  date
    .toISOString()
    .split(/\.[0-9]{3}/, 2)
    .join("");

const mapMonths = {
  1: "January",
  2: "February",
  3: "March",
  4: "April",
  5: "May",
  6: "June",
  7: "July",
  8: "August",
  9: "September",
  10: "October",
  11: "November",
  12: "December",
};

const convertMonthNumberToText = (monthInNumber) => {
  const modifiedMonth = monthInNumber.toString()[0] === "0"
    ? monthInNumber.toString()[1]
    : monthInNumber;
  return mapMonths[modifiedMonth];
};

const amOrpmHour = (hour) => (hour >= 12 && hour !== 24 ? "pm" : "am");

const formatDateForTable = (startDate, endDate) => {
  const start = startDate.split("-");
  const end = endDate.split("-");
  const day = start[2] === end[2] ? start[2] : start[2] + "-" + end[2];
  const month = mapMonths[
    start[1].toString()[0] === "0" ? start[1].toString()[1] : start[1]
  ];
  return month + " " + day + ", " + start[0];
};

const separateDate = (date) => {
  const newDate = new Date(date);
  const year = newDate.getFullYear();
  const month = newDate.getMonth();
  const day = newDate.getDate();
  const hour = newDate.getHours();
  const minute = newDate.getMinutes();
  const seconds = newDate.getSeconds();
  return { year, month, day, hour, minute, seconds };
};

const formatDayMonthYear = (day, month, year) =>
  `${day} ${mapMonths[month + 1]} ${year}`;

const formatHourMinute = (hour, minute) =>
  `${hour}:${minute} ${amOrpmHour(hour)}`;

const formatDateToString = (date) => {
  const separatedDate = separateDate(date);
  const { year, month, day, hour, minute } = separatedDate;
  return `${formatDayMonthYear(day, month, year)} ${formatHourMinute(
    hour,
    minute
  )}`;
};

const formatDateDayFirst = (dateValue) => {
  // date will be like this --> 27 Oct 2022 1:17 AM
  const date = new Date(dateValue);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const hour = date.getHours();
  const minute = date.getMinutes();
  const period = hour >= 12 ? "PM" : "AM";
  const formattedDate = `${day} ${month} ${year} ${hour}:${minute
    .toString()
    .padStart(2, "0")} ${period}`;

  return formattedDate;
};

const formatMonthDayYear = (dateValue) => {
  // date will be like this --> June 24, 2020
  // used for user info page
  const date = new Date(dateValue);
  const day = date.getDate();
  const month = date.toLocaleString("en-US", { month: "short" });
  const year = date.getFullYear();
  const formattedDate = `${month} ${day}, ${year}`;

  return formattedDate;
};

const dayMonthYearDate = (inputDate) => {
  // date will be like this --> 4 June 2020
  const inputDateTime = new Date(inputDate);
  const monthNames = [
    "January", "February", "March", "April", "May", "June",
    "July", "August", "September", "October", "November", "December"
  ];

  const day = inputDateTime.getDate();
  const month = monthNames[inputDateTime.getMonth()];
  const year = inputDateTime.getFullYear();

  const outputDate = `${day} ${month} ${year}`;

  return outputDate;
};

function convertDateForStats(inputDate) {
  // date will be like this --> Oct 16, 03 :30 PM
  const date = new Date(inputDate);
  const month = date.toLocaleString("en-US", { month: "short" });
  const day = date.getDate();
  const hours = date.getHours() % 12 || 12;
  const minutes = date.getMinutes();
  const amOrPm = date.getHours() < 12 ? "AM" : "PM";

  const formattedDate = `${month} ${day}, ${hours.toString().padStart(2, "0")}:${minutes.toString().padStart(2, "0")} ${amOrPm}`;

  return formattedDate;
}

const newFormatDate = (dateString, flagFormat = "both") => {
  // this is the result 14/03/2024 1:31 pm in two lines or only the date or only the time base on flag
  const date = new Date(dateString);

  const day = String(date.getDate()).padStart(2, "0");
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const year = date.getFullYear();

  let hours = date.getHours();
  const minutes = String(date.getMinutes()).padStart(2, "0");
  const ampm = hours >= 12 ? "pm" : "am";

  hours %= 12;
  hours = hours || 12;

  let returnValue = (
    <div>
      {`${day}/${month}/${year}`}
      <br />
      {`${hours}:${minutes} ${ampm}`}
    </div>
  );

  if (flagFormat === "date") {
    returnValue = (
      <div>
        {`${day}/${month}/${year}`}
      </div>
    );
  } else if (flagFormat === "time") {
    returnValue = (
      <div>
        {`${hours}:${minutes} ${ampm}`}
      </div>
    );
  }

  return returnValue;
};

const getYesterdayTomorrow = (daysOffset) => {
  // get yesterday or tomorrow date by passing dayOffset by 1 or -1
  const date = new Date();
  date.setDate(date.getDate() + daysOffset);

  date.setUTCHours(0, 0, 0, 0);

  return date.toISOString().replace(".000", "");
};

// Utility function to handle timezone-aware time calculations for chat
const getTimeAgo = (utcDateString) => {
  if (!utcDateString) return "";

  // If the date string doesn't end with "Z", treat it as UTC and add "Z"
  let dateString = utcDateString;
  if (!dateString.endsWith("Z") && !dateString.includes("+") && !dateString.includes("-", 10)) {
    dateString += "Z";
  }

  // Parse the UTC date string
  const messageDate = new Date(dateString);

  // Calculate the difference in milliseconds
  const now = new Date();
  const diffMs = now - messageDate;

  // Convert to appropriate units
  const diffSeconds = Math.floor(diffMs / 1000);
  const diffMinutes = Math.floor(diffSeconds / 60);
  const diffHours = Math.floor(diffMinutes / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffSeconds < 60) {
    return "a few seconds ago";
  }
  if (diffMinutes < 60) {
    return `${diffMinutes} minute${diffMinutes > 1 ? "s" : ""} ago`;
  }
  if (diffHours < 24) {
    return `${diffHours} hour${diffHours > 1 ? "s" : ""} ago`;
  }
  if (diffDays < 7) {
    return `${diffDays} day${diffDays > 1 ? "s" : ""} ago`;
  }
  return messageDate.toLocaleDateString();
};

// Utility function to format message time with timezone awareness
const formatMessageTime = (utcDateString) => {
  if (!utcDateString) return "";

  try {
    // If the date string doesn't end with "Z", treat it as UTC and add "Z"
    let dateString = utcDateString;
    if (!dateString.endsWith("Z") && !dateString.includes("+") && !dateString.includes("-", 10)) {
      dateString += "Z";
    }

    // Parse the UTC date string
    const date = new Date(dateString);

    // Format using moment with local timezone
    if (moment) {
      return moment(date).format("llll");
    }
    // Fallback to native Date formatting
    return date.toLocaleString();
  } catch (error) {
    console.warn("Error formatting message time:", error);
    return new Date(utcDateString).toLocaleString();
  }
};

export default {
  toBackendDateString,
  toDate,
  toISOStringWithoutMillis,
  convertMonthNumberToText,
  formatDateForTable,
  separateDate,
  formatDateToString,
  formatDayMonthYear,
  formatHourMinute,
  amOrpmHour,
  formatDateDayFirst,
  formatMonthDayYear,
  dayMonthYearDate,
  convertDateForStats,
  newFormatDate,
  getYesterdayTomorrow,
  getTimeAgo,
  formatMessageTime,
};
